import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Layers, Map as MapIcon, BarChart3, PlayCircle, Users, Settings, Leaf, Share2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { PageHero } from "@/components/vig/PageHero";
import VaalMap, { MapLegend } from "@/components/vig/VaalMap";
import { Kpi, KpiStrip, StatusBadge, Tabs } from "@/components/vig/Common";
import { ThemeCard } from "@/components/vig/Cards";
import { JourneyRail } from "@/components/vig/JourneyRail";

const MAP_VIEWS = {
  regional: ["municipalities", "places", "corridors"],
  zones: ["municipalities", "places", "initiatives", "concept"],
  connectivity: ["places", "corridors", "municipalities"],
  infra: ["places", "parcels", "initiatives"],
};

export default function Landing() {
  const { data, setTour } = useApp();
  const nav = useNavigate();
  const [view, setView] = useState("zones");
  const startTour = async () => {
    const r = await api.get("/tour");
    setTour({ active: true, step: 0, steps: r.data, startedAt: Date.now() });
  };
  return (
    <main data-testid="landing-page">
      <PageHero
        code="SOUTH AFRICA · SEDIBENG · THE VAAL REGION"
        title="Vaal Smart City"
        subtitle="A connected region for people, industry and opportunity."
        body="A layered development landscape anchored by strategic location, an established industrial base, a proposed Special Economic Zone, aviation and logistics initiatives, riverfront living and regional connectivity — distinct initiatives, connected by geography."
        minH="min-h-[760px]"
        actions={<>
          <button data-testid="hero-explore-btn" onClick={() => nav("/discover")} className="vig-btn-gold flex items-center gap-2 px-5 py-3 text-sm">Explore the Opportunity <ArrowRight className="h-4 w-4" /></button>
          <button data-testid="hero-guided-demo-btn" onClick={startTour} className="vig-btn-ghost flex items-center gap-2 px-5 py-3 text-sm"><PlayCircle className="h-5 w-5" />Guided Executive Demo<span className="text-xs text-slate-300">(8–12 min)</span></button>
        </>}
      >
        <div className="vig-glass rise d2 w-full p-3 xl:ml-auto xl:w-[560px]" data-testid="landing-map-preview">
          <Tabs testid="landing-map-tabs" value={view} onChange={setView} items={[["regional", "Regional View"], ["zones", "Investment Zones"], ["connectivity", "Connectivity"], ["infra", "Key Infrastructure"]]} />
          <div className="relative mt-2">
            <VaalMap height={390} layers={MAP_VIEWS[view]} onSelect={(k, id) => nav(k === "parcel" ? `/explore?initiative=sez&parcel=${id}` : `/explore?initiative=${id}`)} testid="landing-map" />
            <div className="absolute right-2 top-2 z-[500] hidden w-40 sm:block"><MapLegend /></div>
          </div>
        </div>
      </PageHero>

      <div className="relative z-10 mx-auto -mt-44 max-w-[1600px] px-5 lg:px-8">
        <div className="max-w-[860px]">
          <KpiStrip testid="landing-kpis">
            <Kpi icon={MapPin} value="Strategic" label="Location" sub="Between Gauteng and the Free State" />
            <Kpi icon={Layers} value="817 ha" label="Proposed SEZ designation area" sub="dtic, 2026 [S2]" badge={<StatusBadge status="publicly_reported" />} testid="kpi-817" />
            <Kpi icon={MapIcon} value="3,080 ha" label="Broader land programme" sub="Sedibeng, 2026 [S3]" badge={<StatusBadge status="publicly_reported" />} testid="kpi-3080" />
            <Kpi icon={BarChart3} value="Multi-sector" label="Opportunity" sub={`${data.initiatives.length} mapped initiatives`} />
          </KpiStrip>
        </div>
      </div>

      <section className="mx-auto mt-14 max-w-[1600px] px-5 lg:px-8">
        <div className="text-[12px] tracking-[0.2em] text-slate-400">INVESTMENT AREAS</div>
        <h2 className="mt-1 text-2xl font-semibold text-white md:text-[28px]">Diverse opportunities. A connected future.</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-7">
          {data.themes.map((t) => <ThemeCard key={t.id} theme={t} testid={`landing-theme-${t.id}`} onClick={() => nav(`/discover?theme=${t.id}`)} />)}
        </div>
      </section>

      <section className="relative mt-16 overflow-hidden">
        <img src="/media/night.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#06142a] via-[#06142a]/80 to-[#06142a]/40" />
        <div className="relative mx-auto grid max-w-[1600px] gap-10 px-5 py-16 lg:grid-cols-[1fr_2fr] lg:px-8">
          <div>
            <div className="text-[12px] tracking-[0.2em] text-slate-400">A REGION OF POTENTIAL</div>
            <h2 className="mt-2 text-3xl font-semibold leading-tight text-white">More than a city.<br />A catalyst for inclusive growth.</h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-300">The regional vision brings together infrastructure, economic development and sustainable communities. Each initiative keeps its own sponsor, status and evidence trail.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-white/15">
            {[[Users, "People", "Quality living, jobs and skills development."], [Settings, "Industry", "Competitive, future-ready economic clusters."], [Leaf, "Sustainability", "A greener, more resilient region."], [Share2, "Connectivity", "Integrated transport, digital and infrastructure corridors."]].map(([I, t, d]) => (
              <div key={t} className="lg:pl-6"><I className="h-8 w-8 text-white" strokeWidth={1.3} /><div className="mt-3 text-lg font-semibold">{t}</div><p className="mt-1 text-[13px] text-slate-300">{d}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto mt-14 max-w-[1100px] px-5">
        <div className="text-center text-[12px] tracking-[0.2em] text-slate-400">THE INVESTOR JOURNEY</div>
        <h2 className="mb-8 mt-1 text-center text-2xl font-semibold">From regional interest to a documented next step</h2>
        <JourneyRail active="" />
      </section>
    </main>
  );
}
